import React, { useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import intercomService from '../services/intercomService'

declare global {
  interface Window {
    Intercom: any
    intercomSettings?: any
  }
}

export const IntercomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isMasquerading } = useAuth()

  useEffect(() => {
    // Load Intercom script
    if (!window.Intercom && import.meta.env.VITE_INTERCOM_APP_ID) {
      ;(function () {
        const w = window as any
        const ic = w.Intercom
        if (typeof ic === 'function') {
          ic('reattach_activator')
          ic('update', w.intercomSettings)
        } else {
          const d = document
          const i: any = function () {
            i.c(arguments)
          }
          i.q = []
          i.c = function (args: any) {
            i.q.push(args)
          }
          w.Intercom = i
          const l = function () {
            const s = d.createElement('script')
            s.type = 'text/javascript'
            s.async = true
            s.src = `https://widget.intercom.io/widget/${import.meta.env.VITE_INTERCOM_APP_ID}`
            const x = d.getElementsByTagName('script')[0]
            x.parentNode?.insertBefore(s, x)
          }
          if (document.readyState === 'complete') {
            l()
          } else {
            w.addEventListener('load', l, false)
          }
        }
      })()
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && user && !isMasquerading()) {
      // Initialize Intercom for authenticated users (not masquerading)
      intercomService.initialize()
    } else if (!isAuthenticated || isMasquerading()) {
      // Shutdown Intercom for unauthenticated users or when masquerading
      intercomService.shutdown()
    }

    return () => {
      // Cleanup on unmount
      if (isAuthenticated) {
        intercomService.shutdown()
      }
    }
  }, [isAuthenticated, user, isMasquerading])

  // Track route changes
  useEffect(() => {
    if (isAuthenticated && user && !isMasquerading()) {
      intercomService.update()
    }
  }, [window.location.pathname])

  return <>{children}</>
}
